import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom';
import { useTitlesStats } from './Hooks/useTitlesStats';
import { useAllUserInfo, labelForUid } from './Hooks/useAllUserInfo';
import { useTags } from './Hooks/useTags';

// 2026-09-01: Reports dashboard -- total titles, brand breakdown, per-submitter
// stats, genre/tag breakdowns, missing-key-fields, language split. Visible to every
// portal user (this whole app has no admin/editor tier -- see firestore.rules and
// add_portal_user.py -- `portal: true` is the only gate, checked once at the app
// shell level, same as every other page here). Built into the pre-existing
// `reports/*` route + nav slot (ReportsPage.js was a bare stub, `/app/reports`
// already routed to it) rather than a new "Dashboard" page/route -- that name is
// already the existing title-list home page (DashboardPage.js).
//
// AGGREGATION APPROACH: live client-side (reads the whole `titles` collection and
// computes everything in the browser on page load), not a maintained aggregate doc.
// Checked first: this codebase's only Cloud Function (functions/src/index.ts) is a
// plain HTTPS endpoint for app-feedback submission -- no Firestore triggers exist
// anywhere to incrementally keep a stats doc in sync, so building one now would be
// introducing a genuinely new infrastructure pattern for this one page. At 5,000+
// titles today this is the same read-the-whole-collection approach Add Title's
// duplicate-title check (useAllTitlesLite) already uses every time that page loads,
// so it's proven fine at this scale. Worth revisiting if the catalog reaches the
// tens of thousands, or if Cloud Functions triggers get introduced for some other
// reason first.
//
// SUBMITTER NAME RESOLUTION: uses the existing `userinfo/{uid}` collection (created
// for every portal user by add_portal_user.py, already publicly readable per
// firestore.rules) rather than a new Cloud Function or Firebase Auth Admin API call
// -- there's no way to resolve an arbitrary UID to a name/email from browser code
// (that needs Admin SDK, server-side only), but this project already solved that
// problem for its own purposes; this reuses it rather than inventing a second
// mechanism. See useAllUserInfo.js.
const isFindflixOnly = (t) => t.channels.includes(2) && !t.channels.includes(1);
const isBlackphlixOnly = (t) => t.channels.includes(1) && !t.channels.includes(2);
const isDual = (t) => t.channels.includes(1) && t.channels.includes(2);

function StatCard({ label, value, sub }) {
    return (
        <div className="bg-white overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{value.toLocaleString()}</dd>
            {sub && <dd className="mt-1 text-xs text-gray-400">{sub}</dd>}
        </div>
    );
}

// Ranked horizontal bar list -- used for both Genre and Tag breakdowns. Deliberately
// not a pie/donut: genres and tags are multi-valued per title (a title can carry
// several of each), so counts don't sum to the filtered total the way brand/type do
// -- a pie chart here would misleadingly imply they should.
function BarList({ items, maxCount, emptyLabel }) {
    if (items.length === 0) {
        return <p className="text-sm text-gray-400 italic">{emptyLabel}</p>;
    }

    return (
        <div className="space-y-2">
            {items.map((item) => (
                <div key={item.key} className="flex items-center gap-x-3">
                    <div className="w-40 shrink-0 truncate text-sm text-gray-700" title={item.label}>{item.label}</div>
                    <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                        <div className="bg-indigo-600 h-4 rounded" style={{ width: `${maxCount > 0 ? (item.count / maxCount) * 100 : 0}%` }} />
                    </div>
                    <div className="w-12 shrink-0 text-right text-sm text-gray-500 tabular-nums">{item.count.toLocaleString()}</div>
                </div>
            ))}
        </div>
    );
}

export default function ReportsPage({ authUser }) {
    // HOOKS -- always called unconditionally (rules-of-hooks); loading state is
    // handled below by treating undefined collections as empty until they resolve,
    // not by early-returning before these run.
    const titlesRaw = useTitlesStats();
    const userinfoByUid = useAllUserInfo();
    const tags = useTags();

    // FILTERS -- "search" on this page means narrowing these dashboard numbers
    // (brand / type / submitter), not a free-text title search (that's a separate,
    // out-of-scope ask). All sections below derive from the SAME filtered set, so
    // e.g. picking FindFlix + Series narrows literally everything on the page to
    // that slice -- simplest mental model, matches the "just the TV series
    // breakdown" example this was scoped against.
    const [brandFilter, setBrandFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [submitterFilter, setSubmitterFilter] = useState('all');
    const [expandedMissing, setExpandedMissing] = useState(null);

    const isLoading = titlesRaw === undefined || userinfoByUid === undefined || tags === undefined;
    // useMemo (not a plain `titlesRaw || []`) so the empty-array fallback while
    // loading is a stable reference -- otherwise every useMemo below that depends on
    // `titles` would recompute on every render, since a literal [] is a fresh
    // reference each time.
    const titles = useMemo(() => titlesRaw || [], [titlesRaw]);

    const tagsByGuid = useMemo(() => {
        const map = {};
        (tags || []).forEach(t => { map[t.guid] = t.title; });
        return map;
    }, [tags]);

    const filteredTitles = useMemo(() => titles.filter(t => {
        if (brandFilter === 'findflix' && !isFindflixOnly(t)) return false;
        if (brandFilter === 'blackphlix' && !isBlackphlixOnly(t)) return false;
        if (brandFilter === 'dual' && !isDual(t)) return false;
        if (typeFilter === 'movie' && t.kind !== 1) return false;
        if (typeFilter === 'series' && t.kind !== 2) return false;
        if (submitterFilter !== 'all' && t.creator !== submitterFilter) return false;
        return true;
    }), [titles, brandFilter, typeFilter, submitterFilter]);

    const total = filteredTitles.length;
    const findflixCount = useMemo(() => filteredTitles.filter(isFindflixOnly).length, [filteredTitles]);
    const blackphlixCount = useMemo(() => filteredTitles.filter(isBlackphlixOnly).length, [filteredTitles]);
    const dualCount = useMemo(() => filteredTitles.filter(isDual).length, [filteredTitles]);
    const movieCount = useMemo(() => filteredTitles.filter(t => t.kind === 1).length, [filteredTitles]);
    const seriesCount = useMemo(() => filteredTitles.filter(t => t.kind === 2).length, [filteredTitles]);

    const crossTab = useMemo(() => [
        { label: 'FindFlix', test: isFindflixOnly },
        { label: 'BlackPhlix', test: isBlackphlixOnly },
        { label: 'Dual-brand', test: isDual },
    ].map(row => {
        const rows = filteredTitles.filter(row.test);
        return { label: row.label, total: rows.length, movies: rows.filter(t => t.kind === 1).length, series: rows.filter(t => t.kind === 2).length };
    }), [filteredTitles]);

    // Submitter dropdown options: stable, computed from the FULL (unfiltered) set so
    // the list doesn't reorder or shrink under the user as they change other
    // filters. Union of userinfo docs (includes portal users with zero titles so
    // far, e.g. a brand-new curator) and any creator uid on a title that somehow has
    // no userinfo doc at all (defensive -- not currently the case, per labelForUid's
    // fallback).
    const allSubmitterUids = useMemo(() => {
        const set = new Set(Object.keys(userinfoByUid || {}));
        titles.forEach(t => { if (t.creator) set.add(t.creator); });
        return [...set].sort((a, b) => labelForUid(a, userinfoByUid).localeCompare(labelForUid(b, userinfoByUid)));
    }, [titles, userinfoByUid]);

    // Submitter table: same uid universe as the dropdown (so a portal user with zero
    // titles still shows as a 0/0/0 row rather than being invisible), counts against
    // the currently filtered set. Sortable by total isn't wired up as a click
    // (default sort IS by total desc, per the brief -- "cheap to add" but not
    // strictly necessary at 7 submitters where Chris is first by a wide margin).
    const submitterRows = useMemo(() => allSubmitterUids.map(uid => {
        const rows = filteredTitles.filter(t => t.creator === uid);
        return {
            uid,
            label: labelForUid(uid, userinfoByUid),
            total: rows.length,
            movies: rows.filter(t => t.kind === 1).length,
            series: rows.filter(t => t.kind === 2).length,
        };
    }).sort((a, b) => b.total - a.total), [allSubmitterUids, filteredTitles, userinfoByUid]);

    // Genre breakdown -- plain strings on the title doc. Deliberately NOT
    // cross-checked against the `genres` collection: the orphaned-genre-string
    // cleanup (some titles carry a genre string with no matching genres doc) is
    // explicitly deferred to a separate follow-up, so this tallies whatever strings
    // actually appear on titles today, unfiltered by whether they're "valid".
    const genreCounts = useMemo(() => {
        const map = {};
        filteredTitles.forEach(t => t.genres.forEach(g => { map[g] = (map[g] || 0) + 1; }));
        return Object.entries(map).map(([key, count]) => ({ key, label: key, count })).sort((a, b) => b.count - a.count);
    }, [filteredTitles]);

    // Tag breakdown -- titles store tag GUIDs (not strings, unlike genres), resolved
    // to display names via useTags(). A guid with no matching tag doc (same class of
    // drift as orphaned genres, not this build's concern) falls back to a visible
    // "Unknown tag" label rather than silently disappearing from the count.
    const tagCounts = useMemo(() => {
        const map = {};
        filteredTitles.forEach(t => t.tags.forEach(guid => { map[guid] = (map[guid] || 0) + 1; }));
        return Object.entries(map)
            .map(([guid, count]) => ({ key: guid, count, label: tagsByGuid[guid] || `Unknown tag (${guid.slice(0, 6)}…)` }))
            .sort((a, b) => b.count - a.count);
    }, [filteredTitles, tagsByGuid]);

    const maxGenreCount = genreCounts.length > 0 ? genreCounts[0].count : 1;
    const maxTagCount = tagCounts.length > 0 ? tagCounts[0].count : 1;

    // Missing-key-fields: the four Pascal confirmed live counts for (director,
    // rating, genres, tags), plus two bonus checks (synopsis, poster) per his
    // explicit invitation to add more if something else looked commonly empty.
    // Each is click-to-expand into the actual list of affected titles, linking to
    // their detail pages, rather than just a static count.
    const missingChecks = useMemo(() => ([
        { key: 'director', label: 'Missing a director', test: t => !t.hasDirector },
        { key: 'rating', label: 'Missing a rating', test: t => !t.hasRating },
        { key: 'genres', label: 'Missing genres', test: t => t.genres.length === 0 },
        { key: 'tags', label: 'Missing tags', test: t => t.tags.length === 0 },
        { key: 'synopsis', label: 'Missing a synopsis', test: t => !t.hasSynopsis },
        { key: 'poster', label: 'Missing a poster', test: t => !t.hasPoster },
    ]).map(c => ({ ...c, matches: filteredTitles.filter(c.test) })), [filteredTitles]);

    // Language split -- English vs. Not English ONLY, per Pascal's explicit call
    // (not a full per-language-code breakdown -- 54 distinct non-English codes
    // exist, most with a handful of titles each, not useful as a UI). Titles with no
    // language set at all are lumped into "Not English" (recommended choice, noted
    // below) rather than a third "Unknown" bucket -- simpler for a binary split, and
    // technically correct since they're not confirmed English either.
    const englishCount = useMemo(() => filteredTitles.filter(t => t.language === 'en').length, [filteredTitles]);
    const undefinedLanguageCount = useMemo(() => filteredTitles.filter(t => t.language === undefined).length, [filteredTitles]);
    const notEnglishCount = total - englishCount;

    const toggleMissing = (key) => setExpandedMissing(expandedMissing === key ? null : key);

    return (
        <>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                    <h1 className="my-0 text-2xl font-bold text-gray-900 sm:text-3xl">Reports</h1>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Live counts across the whole titles catalog. Filters below narrow every section on this page.</p>
                </div>
            </div>

            {isLoading && <div className="mt-6 text-sm text-gray-500">Loading…</div>}

            {!isLoading && <>
                {/* Filters */}
                <div className="mt-6 bg-white shadow rounded-lg px-4 py-4 sm:px-6 flex flex-wrap items-end gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Brand</label>
                        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="rounded-md border-0 py-1.5 pl-2 pr-8 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
                            <option value="all">All brands</option>
                            <option value="findflix">FindFlix</option>
                            <option value="blackphlix">BlackPhlix</option>
                            <option value="dual">Dual-brand</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-md border-0 py-1.5 pl-2 pr-8 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
                            <option value="all">All types</option>
                            <option value="movie">Movies</option>
                            <option value="series">Series</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Submitter</label>
                        <select value={submitterFilter} onChange={(e) => setSubmitterFilter(e.target.value)} className="rounded-md border-0 py-1.5 pl-2 pr-8 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
                            <option value="all">All submitters</option>
                            {allSubmitterUids.map(uid => (
                                <option key={uid} value={uid}>{labelForUid(uid, userinfoByUid)}</option>
                            ))}
                        </select>
                    </div>
                    {(brandFilter !== 'all' || typeFilter !== 'all' || submitterFilter !== 'all') && (
                        <button type="button" onClick={() => { setBrandFilter('all'); setTypeFilter('all'); setSubmitterFilter('all'); }} className="text-sm text-indigo-600 hover:text-indigo-800 underline">
                            Clear filters
                        </button>
                    )}
                </div>

                {/* Top-level counters */}
                <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard label="Total titles" value={total} />
                    <StatCard label="FindFlix" value={findflixCount} sub="FindFlix-only" />
                    <StatCard label="BlackPhlix" value={blackphlixCount} sub="BlackPhlix-only" />
                    <StatCard label="Dual-brand" value={dualCount} sub="On both" />
                </dl>

                {/* 3-way brand split bar */}
                {total > 0 && <div className="mt-4 bg-white shadow rounded-lg px-4 py-4 sm:px-6">
                    <div className="flex h-4 rounded overflow-hidden">
                        {findflixCount > 0 && <div className="bg-indigo-600" style={{ width: `${(findflixCount / total) * 100}%` }} title={`FindFlix: ${findflixCount}`} />}
                        {blackphlixCount > 0 && <div className="bg-purple-500" style={{ width: `${(blackphlixCount / total) * 100}%` }} title={`BlackPhlix: ${blackphlixCount}`} />}
                        {dualCount > 0 && <div className="bg-amber-400" style={{ width: `${(dualCount / total) * 100}%` }} title={`Dual-brand: ${dualCount}`} />}
                    </div>
                    <div className="mt-2 flex gap-x-4 text-xs text-gray-500">
                        <span><span className="inline-block w-2 h-2 rounded-full bg-indigo-600 mr-1" />FindFlix</span>
                        <span><span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-1" />BlackPhlix</span>
                        <span><span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />Dual-brand</span>
                    </div>
                </div>}

                {/* Movies vs series */}
                <div className="mt-6 bg-white shadow rounded-lg px-4 py-5 sm:px-6">
                    <h2 className="text-lg font-medium text-gray-900">Movies vs. series</h2>
                    <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-2 max-w-md">
                        <StatCard label="Movies" value={movieCount} />
                        <StatCard label="Series" value={seriesCount} />
                    </dl>

                    <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Movies</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Series</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {crossTab.map((row, idx) => (
                                    <tr key={row.label} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.label}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{row.movies.toLocaleString()}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{row.series.toLocaleString()}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{row.total.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Per-submitter table */}
                <div className="mt-6 bg-white shadow rounded-lg px-4 py-5 sm:px-6">
                    <h2 className="text-lg font-medium text-gray-900">By submitter</h2>
                    <p className="mt-1 text-sm text-gray-500">Sorted by total. Includes portal users with zero titles so far.</p>
                    <div className="mt-3 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitter</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Movies</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Series</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {submitterRows.map((row, idx) => (
                                    <tr key={row.uid} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.label}{row.uid === authUser.uid && <span className="ml-2 text-xs text-indigo-600">(you)</span>}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{row.movies.toLocaleString()}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{row.series.toLocaleString()}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{row.total.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Genre breakdown */}
                <div className="mt-6 bg-white shadow rounded-lg px-4 py-5 sm:px-6">
                    <h2 className="text-lg font-medium text-gray-900">Genres</h2>
                    <p className="mt-1 text-sm text-gray-500">A title can carry more than one genre, so these counts don't sum to the total above.</p>
                    <div className="mt-3">
                        <BarList items={genreCounts} maxCount={maxGenreCount} emptyLabel="No genres on any title in this filter." />
                    </div>
                </div>

                {/* Tag breakdown */}
                <div className="mt-6 bg-white shadow rounded-lg px-4 py-5 sm:px-6">
                    <h2 className="text-lg font-medium text-gray-900">Tags</h2>
                    <p className="mt-1 text-sm text-gray-500">Same as genres -- multi-valued, doesn't sum to the total.</p>
                    <div className="mt-3">
                        <BarList items={tagCounts} maxCount={maxTagCount} emptyLabel="No tags on any title in this filter." />
                    </div>
                </div>

                {/* Missing key fields */}
                <div className="mt-6 bg-white shadow rounded-lg px-4 py-5 sm:px-6">
                    <h2 className="text-lg font-medium text-gray-900">Missing key fields</h2>
                    <p className="mt-1 text-sm text-gray-500">Titles that won't display key data properly in the app. Click a count to see the titles.</p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {missingChecks.map((check) => (
                            <div key={check.key}>
                                <button type="button" onClick={() => toggleMissing(check.key)} className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg px-4 py-3">
                                    <div className="text-2xl font-semibold text-gray-900">{check.matches.length.toLocaleString()}</div>
                                    <div className="text-sm text-gray-500">{check.label}</div>
                                </button>
                                {expandedMissing === check.key && (
                                    <div className="mt-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                                        {check.matches.length === 0 && <p className="p-3 text-sm text-gray-400 italic">None in this filter.</p>}
                                        {check.matches.map((t) => (
                                            <Link key={t.id} to={`/app/title/${t.id}`} className="block px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 truncate">
                                                {t.title || '(untitled)'}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Language split */}
                <div className="mt-6 bg-white shadow rounded-lg px-4 py-5 sm:px-6 mb-8">
                    <h2 className="text-lg font-medium text-gray-900">Language</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        English vs. everything else. {undefinedLanguageCount > 0 && `${undefinedLanguageCount.toLocaleString()} of the titles counted as "Not English" have no language set at all.`}
                    </p>
                    <dl className="mt-3 grid grid-cols-2 gap-4 max-w-md">
                        <StatCard label="English" value={englishCount} />
                        <StatCard label="Not English" value={notEnglishCount} />
                    </dl>
                    {total > 0 && <div className="mt-3 flex h-4 rounded overflow-hidden max-w-md">
                        {englishCount > 0 && <div className="bg-emerald-500" style={{ width: `${(englishCount / total) * 100}%` }} />}
                        {notEnglishCount > 0 && <div className="bg-rose-400" style={{ width: `${(notEnglishCount / total) * 100}%` }} />}
                    </div>}
                </div>
            </>}
        </>
    )
}
